import { useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Alert, Box, Stack } from "@mui/material"
import { useGetLojaAtualQuery } from "@/api"
import type { RootState } from "@/store"
import { ConfigTotem } from "@/components/config-totem"
import { KioskDispositivo } from "@/components/kiosk-dispositivo"
import { PwaKioskInstall } from "@/components/pwa-kiosk-banner"
import { SobreCardapio } from "@/components/sobre-cardapio"
import { TecladoTela } from "@/components/teclado-tela"
import AvisoInatividade from "@/components/totem/aviso-inatividade"
import { useInatividadeKiosk } from "@/components/totem/use-inatividade-kiosk"
import { useAvisarOcioso } from "@/lib/atualizacao-totem"
import { haOperacaoCritica } from "@/lib/operacao-critica"
import { imprimeNoAparelho, opcoesImpressaoTotem, totemImprimeAlgo } from "@/lib/impressao-totem"
import { getLocalImpressaoTotem } from "@/lib/totem-config"
import { PedidoConfirmadoPage } from "@/pages/pedido-confirmado"
import Atracao from "./atracao"
import Carrinho from "./carrinho"
import Catalogo from "./catalogo"
import Consumo from "./consumo"
import Finalizar from "./finalizar"
import { MS_OCIOSO_CONSUMO } from "./constantes"
import { reiniciarAtendimento } from "./navegacao"
import { inatividadeDaTela, useVigiaTotem } from "./use-vigia-totem"

export default function TotemPedidos(): JSX.Element {
    const dispatch = useDispatch()
    const { currentView, pedidoId } = useSelector((s: RootState) => s.totemPedidos)
    const { data: loja } = useGetLojaAtualQuery()
    const [impressoraReconferida, setImpressoraReconferida] = useState(0)
    const modoConsumo = loja?.configuracao.totemConsumo ?? 'AMBOS'
    const perguntaConsumo = modoConsumo === 'AMBOS'

    const semImpressora = useMemo(() => {
        if (!loja) return false

        if (!totemImprimeAlgo(opcoesImpressaoTotem(loja.configuracao))) return false

        if (imprimeNoAparelho()) return false

        return !getLocalImpressaoTotem().trim() && !loja.configuracao.totemLocalImpressaoConfigurado
    }, [loja, impressoraReconferida])

    useAvisarOcioso(currentView === 'VIEW_ATRACAO' && !haOperacaoCritica())

    const vigia = useVigiaTotem()
    const temItens = useSelector((s: RootState) => s.carrinho.itens.length > 0)
    const catalogoMontado = currentView === 'VIEW_CATALOGO' || currentView === 'VIEW_CARRINHO' || currentView === 'VIEW_FINALIZAR'
    const inatividade = inatividadeDaTela(currentView, temItens)

    useInatividadeKiosk(
        inatividade === 'SILENCIOSA' && vigia.ativo,
        () => reiniciarAtendimento(dispatch),
        MS_OCIOSO_CONSUMO
    )

    if (semImpressora) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="warning">
                    Este totem ainda não tem <strong>impressora configurada</strong> e por isso não consegue
                    imprimir a senha do pedido. Escolha a impressora para liberar os pedidos — é preciso o PIN da
                    loja.
                </Alert>

                <ConfigTotem semBotao obrigatorio onConfigurado={() => setImpressoraReconferida((n) => n + 1)} />
            </Box>
        )
    }

    return (
        <Stack direction="column" sx={{ alignItems: 'center', width: '100%', minHeight: '100dvh' }}>
            {currentView === 'VIEW_ATRACAO' && <Atracao modo={modoConsumo} />}
            {currentView === 'VIEW_CONSUMO' && <Consumo />}
            {catalogoMontado && <Catalogo podeTrocarConsumo={perguntaConsumo} />}
            {currentView === 'VIEW_CARRINHO' && <Carrinho bloqueado={false} />}
            {currentView === 'VIEW_FINALIZAR' && <Finalizar />}

            {currentView === 'VIEW_CONFIRMADO' && (
                <PedidoConfirmadoPage idPedido={pedidoId ?? undefined} aoEncerrar={() => reiniciarAtendimento(dispatch)} />
            )}

            {inatividade !== 'SILENCIOSA' && (
                <AvisoInatividade
                    ativo={inatividade === 'COM_AVISO' && vigia.ativo}
                    msInatividade={vigia.msInatividade}
                    aoExpirar={() => reiniciarAtendimento(dispatch)}
                    mensagem={(segundos) => (
                        <>
                            Sem resposta, seu pedido será descartado e o totem volta ao início em{' '}
                            <strong>{segundos}s</strong>.
                        </>
                    )}
                    acaoSecundaria={{ rotulo: 'Recomeçar agora', aoClicar: () => reiniciarAtendimento(dispatch) }}
                />
            )}

            {currentView !== 'VIEW_ATRACAO' && !catalogoMontado && <SobreCardapio />}

            <ConfigTotem semBotao />
            <KioskDispositivo />
            <TecladoTela />
            <PwaKioskInstall />
        </Stack>
    )
}
